import type { Project } from '@/services/projectTypes'
import type { StudyRow } from './StudiesTable'
import type { StudyStatus } from './dashboardTypes'

/** Locale-aware "x minutes ago" formatter for `updated_at` timestamps. */
export function createRelativeFormatter(language: string): (iso: string) => string {
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' })
  return (iso: string) => {
    const ts = iso ? new Date(iso).getTime() : NaN
    if (!Number.isFinite(ts)) return '—'
    const mins = Math.round((Date.now() - ts) / 60000)
    if (mins < 60) return rtf.format(-mins, 'minute')
    const hours = Math.round(mins / 60)
    if (hours < 24) return rtf.format(-hours, 'hour')
    return rtf.format(-Math.round(hours / 24), 'day')
  }
}

/**
 * Real Project → StudiesTable row view model. Shared by the dashboard and the
 * Projects screen. The responses target is the researcher's `target_respondents`
 * persisted on the project; `client` is the real value persisted on the project
 * (StudiesTable shows an "internal" fallback when it is empty).
 */
export function toStudyRow(
  project: Project,
  formatDate: (d: Date) => string,
  formatRelative: (iso: string) => string,
): StudyRow {
  // A draft (still being built in the wizard) takes precedence over the
  // active/done split, which only applies to launched studies.
  const status: StudyStatus =
    project.status === 'draft' ? 'draft' : project.is_active ? 'active' : 'done'
  const n = project.participants_count
  const target = project.target_respondents || n
  return {
    id: project.id,
    name: project.name,
    client: project.client?.trim() ?? '',
    status,
    responses: { n, t: target },
    createdLabel: formatDate(new Date(project.created_at)),
    lastActivityLabel: formatRelative(project.updated_at),
    sharedWithTeam: project.visibility === 'team',
    // Only set for other people's studies, so the column stays quiet for solo work.
    authorLabel: project.is_own === false ? project.created_by_name : undefined,
  }
}
